import { useContext } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import ImageUpload from "components/ImageUpload";
import FormWrapper from "components/FormWrapper";
import ResetAndSubmit from "components/ResetAndSubmit";
import * as Yup from "yup";
import TextArea from "components/TextArea";
import { AppContext } from "state";
import { getImageValidationSchema } from "constants/validationSchemas";
import Card from "components/OnboardingFormCard";
import { generateUniqueId } from "utils"; // <-- make sure this exists

const validationSchema = Yup.object({
  recognitions: Yup.array().of(
    Yup.object({
      RECOGNITION_IMAGE: getImageValidationSchema("Recognition image is required"),
      RECOGNITION_ACHIEVEMENT: Yup.string().required("Recognition achievement is required"),
      RECOGNITION_ISSUED_BY: Yup.string().required("Recognition issued by is required"),
      RECOGNITION_YEAR: Yup.string().required("Recognition year is required"),
    })
  )
});

const NewRecognitions = ({
  handleNavigate,
  resetFormData,
  disableForm
}: {
  handleNavigate,
  resetFormData?: any,
  titleAndDesNotRequired?: boolean,
  disableForm?: boolean
}) => {
  const { globalData } = useContext(AppContext);

  const createEmptyRecognition = () => ({
    ID: generateUniqueId(),
    DRAWER: false,
    RECOGNITION_IMAGE: "",
    RECOGNITION_ACHIEVEMENT: "",
    RECOGNITION_ISSUED_BY: "",
    RECOGNITION_YEAR: "",
    RECOGNITION_DESCRIPTION: "",
  });

  const loadInitialData = () => {
    const savedData = JSON.parse(sessionStorage.getItem("recognitions")) || [];
    return savedData.length ? savedData : [createEmptyRecognition()];
  };

  return (
    <Formik
      initialValues={{ recognitions: loadInitialData() }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        console.log("recognitions form submission values:", values.recognitions);
        sessionStorage.setItem("recognitions", JSON.stringify(values.recognitions));
        handleNavigate();
      }}
    >
      {({ values, handleSubmit, resetForm, errors, setFieldValue }) => (
        <FormWrapper
          title={"Celebrate Your Institution's Achievements!"}
          description={"Show off the accolades, Awards, and recognitions that makes your institution stand out. this is where your institution's acheivements take center stage!."}
          addMore={
            !!values.recognitions?.[0]?.RECOGNITION_ACHIEVEMENT && values.recognitions.length < 5
              ? () =>
                  setFieldValue("recognitions", [
                    ...(values.recognitions || []),
                    { ...createEmptyRecognition(), DRAWER: true }
                  ])
              : ""
          }
          addMoreLabel={"Add Achievements"}
          childern={
            <Form onSubmit={handleSubmit}>
              <div>
                <div className="rounded-2xl my-1 mx-1">
                  <FieldArray name="recognitions">
                    {({ push, remove, form }) => (
                      <div className="grid grid-cols-4 gap-4 sm:grid-cols-1 md:grid-cols-1">
                        {values.recognitions.map((recognition, index) => (
                          <Card
                            key={recognition?.ID || "recognition-" + index}
                            isError={Boolean(errors?.recognitions?.[index])}
                            index={index}
                            handleOpenDrawer={() => form.setFieldValue(`recognitions.${index}.DRAWER`, true)}
                            handleCloseDrawer={() => form.setFieldValue(`recognitions.${index}.DRAWER`, false)}
                            onDelete={() => {
                              if (values.recognitions?.length > 1) {
                                remove(index);
                              } else {
                                remove(index);
                                push({ ...createEmptyRecognition(), DRAWER: false });
                              }
                            }}
                            element={recognition}
                          >
                            <div className="flex flex-col gap-4">
                              <div className="w-full">
                                <Field name={`recognitions.${index}.RECOGNITION_IMAGE`}>
                                  {({ field, form }) => (
                                    <ImageUpload
                                      disable={disableForm}
                                      label="Upload Recognition Image"
                                      maxSize={5}
                                      labelClassNames="text-[2vh]"
                                      containerHeight={{
                                        width: "7vh",
                                        height: "7vh",
                                      }}
                                      imageValue={field.value}
                                      onChange={(item) =>
                                        form.setFieldValue(
                                          `recognitions.${index}.RECOGNITION_IMAGE`,
                                          item
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                              </div>
                              <div className="w-full">
                                <Input
                                  disable={disableForm}
                                  label="Award Name"
                                  name={`recognitions.${index}.RECOGNITION_ACHIEVEMENT`}
                                  type="text"
                                  placeHolder="Enter name of the Award"
                                  required
                                />
                              </div>
                              <div className="w-full">
                                <Input
                                  disable={disableForm}
                                  label="Issued By"
                                  name={`recognitions.${index}.RECOGNITION_ISSUED_BY`}
                                  type="text"
                                  placeHolder="Enter the Name of the Issuer"
                                  required
                                />
                              </div>
                              <div className="w-full">
                                <Dropdown
                                  disable={disableForm}
                                  name={`recognitions.${index}.RECOGNITION_YEAR`}
                                  label="Year of Issue"
                                  placeHolder="Select Year of Issue"
                                  required
                                  options={[
                                    { value: "2023", label: "2023" },
                                    { value: "2022", label: "2022" },
                                    { value: "2021", label: "2021" },
                                  ]}
                                />
                              </div>
                              <div className="w-full">
                                <TextArea
                                  disabled={
                                    disableForm ||
                                    !(
                                      recognition?.RECOGNITION_ACHIEVEMENT &&
                                      recognition?.RECOGNITION_ISSUED_BY &&
                                      recognition?.RECOGNITION_YEAR
                                    )
                                  }
                                  label="Description"
                                  name={`recognitions.${index}.RECOGNITION_DESCRIPTION`}
                                  autoGenerate={true}
                                  generate={{
                                    category: "recognitions",
                                    kwargs: {
                                      name: recognition?.RECOGNITION_ACHIEVEMENT,
                                      issuedBy: recognition?.RECOGNITION_ISSUED_BY,
                                      year: recognition?.RECOGNITION_YEAR,
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>
              {!disableForm && (
                <ResetAndSubmit
                  skipButtonAndFunction={() => {
                    handleNavigate()
                  }}
                  submitToolTip={
                    !Boolean(
                      values?.recognitions?.filter((item) => !!item?.RECOGNITION_ACHIEVEMENT)?.length
                    )
                      ? "Please fill in at least one recognition with details and images"
                      : Boolean(errors?.recognitions)
                      ? "Please fill in all required fields"
                      : ""
                  }
                  isError={
                    !Boolean(
                      values?.recognitions?.filter((item) => !!item?.RECOGNITION_ACHIEVEMENT)?.length
                    ) || Boolean(errors?.recognitions)
                  }
                  resetForm={() => {
                    resetForm();
                    resetFormData();
                  }}
                />
              )}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default NewRecognitions;
